import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CollectionPreferences,
  Container,
  Header,
  MultiselectProps,
  Pagination,
  SpaceBetween,
  StatusIndicator,
  Table,
  Tabs,
} from '@amzn/awsui-components-react';
import {
  Event,
  GetParticipationsByEventAndAttendeesCommandOutput,
  IdentifierType,
  MultiShiftParticipation,
  Participation,
  Shift,
  ShiftType,
} from '@amzn/red-velvet-api';
import type {
  FailedIdentifier,
  AlreadyTrackedAttendeeRow,
  ValidAttendee,
} from './attendeeTableColumns';
import {
  MultiShiftAttendee,
  useMultiShiftAttendeeColumns,
  useValidAttendeeColumns,
  useInvalidEntryColumns,
  useAlreadyTrackedShiftColumns,
} from './attendeeTableColumns';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { callQueryClientSingletonRedVelvetQuery } from '../../../hooks/singleItemRedVelvetQuery';
import { useSearchParams } from 'react-router-dom';
import { useFeatures, getEnabledFeatures } from '../../../hooks/features';
import { mergeValidationResponses } from './utils/validationMerger';
import { useBulkParticipantSessionStorage } from '../../../hooks/attendance/bulkParticipantSessionStorage';
import { useUser } from '../../../hooks/user';
import { publishKatalMetric } from '../../katalAnalytics';
import { asDateTimeString, formatShiftDate, formatShiftTime } from '../../../utils/dateFormatting';
import { useTimezone } from '../../../hooks/timezone';
import { Amazonian } from '../../../utils/attendance/peopleSearch';

enum ModalTab {
  INVALID = 'invalidTab',
  MULTI_SHIFT = 'multiShiftTab',
  VALID = 'validTab',
  SKIPPED = 'skippedTab',
}

const DEFAULT_PAGE_SIZE = 100;

interface BulkUploadValidateAttendeesContentProps {
  validationData: GetParticipationsByEventAndAttendeesCommandOutput;
  identifierType: IdentifierType;
  event: Partial<Event>; // Supports unlisted events where eventId may not exist yet
  shifts: Shift[];
  shiftIdFromTrackAttendanceWizard?: string; // this only defines when modal is opened at Shift level Attendance tracking wizard
  onCancel: () => void;
  onPrevious: () => void;
  onComplete?: () => void;
}

export function BulkUploadValidateAttendeesContent({
  validationData,
  identifierType,
  event,
  shifts,
  shiftIdFromTrackAttendanceWizard,
  onCancel,
  onPrevious,
  onComplete,
}: BulkUploadValidateAttendeesContentProps) {
  const namespaces = ['attendance', 'translation'];
  const { t, i18n } = useTranslation(namespaces);
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const queryClient = useQueryClient();
  const { updateBulkParticipants } = useBulkParticipantSessionStorage();
  const user = useUser();
  const timezone = useTimezone();

  const openFromTrackAttendanceWizard = !!shiftIdFromTrackAttendanceWizard;
  // Unlisted event mode: no event exists yet, so no shifts available
  const isUnlistedEventMode = !event?.eventId && shifts.length === 0;

  const [multiShiftAttendees, setMultiShiftAttendees] = useState<MultiShiftAttendee[]>([]);
  const [singleShiftEdits, setSingleShiftEdits] = useState<Map<string, Shift>>(new Map());
  const [activeTabId, setActiveTabId] = useState<ModalTab>(
    validationData.failedIdentifiers.length > 0 ? ModalTab.INVALID : ModalTab.VALID
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  // Pagination state (shared across all tabs)
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const handleTabChange = ({ detail }: { detail: { activeTabId: string } }): void => {
    // Cloudscape Tabs component returns activeTabId as string, so we cast to our enum
    setActiveTabId(detail.activeTabId as ModalTab);
    // Reset to page 1 when switching tabs
    setCurrentPageIndex(1);
  };

  const initialFailedIdentifiers = useMemo(() => {
    const getErrorMessage = (type: IdentifierType): string => {
      switch (type) {
        case IdentifierType.ALIAS:
          return t('bulkUpload.validation.errors.noEmployeeAlias');
        case IdentifierType.EMPLOYEE_ID:
          return t('bulkUpload.validation.errors.noEmployeeId');
        case IdentifierType.BADGE_ID:
          return t('bulkUpload.validation.errors.noEmployeeBadgeId');
      }
    };

    return validationData.failedIdentifiers.map((identifier, index) => ({
      id: index,
      correctedValue: identifier,
      originalValue: identifier,
      error: getErrorMessage(identifierType),
      input: identifier,
      isEditing: false,
      isResolved: false,
      isValidating: false,
    }));
  }, [validationData.failedIdentifiers, identifierType, t]);

  const [failedIdentifiers, setFailedIdentifiers] =
    useState<FailedIdentifier[]>(initialFailedIdentifiers);
  const [resolvedResponses, setResolvedResponses] = useState<
    GetParticipationsByEventAndAttendeesCommandOutput[]
  >([]);

  const mergedValidationData = useMemo(
    () => mergeValidationResponses(validationData, resolvedResponses, failedIdentifiers),
    [validationData, resolvedResponses, failedIdentifiers]
  );

  // Build map of already-tracked shifts by alias (shared by multi-shift and valid tabs)
  const alreadyTrackedShiftsByAlias = useMemo(() => {
    const map = new Map<string, Set<string>>();
    mergedValidationData.trackedParticipations.forEach((p) => {
      // Only include self-tracked shifts (where attendanceUpdatedByAlias === volunteerAlias)
      if (
        p.volunteerAlias &&
        p.volunteerAlias.trim() !== '' &&
        p.shiftId &&
        p.attendanceUpdatedByAlias === p.volunteerAlias
      ) {
        const existingShifts = map.get(p.volunteerAlias) || new Set<string>();
        existingShifts.add(p.shiftId);
        map.set(p.volunteerAlias, existingShifts);
      }
    });
    return map;
  }, [mergedValidationData.trackedParticipations]);

  // Initialize/update multi-shift attendees from backend data
  useEffect(() => {
    const backendMultiShift = buildAttendeesFromMultiShift(
      mergedValidationData.multiShiftParticipations || [],
      shifts,
      alreadyTrackedShiftsByAlias
    );

    // Merge with existing to preserve manual changes
    setMultiShiftAttendees((prevAttendees) =>
      mergeAttendeesPreservingManualChanges(backendMultiShift, prevAttendees)
    );
  }, [mergedValidationData.multiShiftParticipations, shifts, alreadyTrackedShiftsByAlias]);

  // Build valid attendee rows (one row per shift signup)
  const validAttendeeRows = useMemo((): ValidAttendee[] => {
    // Build map of tracked participations by alias for users with ONLY already-tracked attendance
    const trackedParticipationsByAlias = new Map<string, Participation[]>();

    mergedValidationData.trackedParticipations
      .filter((p) => p.volunteerAlias && p.volunteerAlias.trim() !== '')
      .forEach((p) => {
        const existingParticipations = trackedParticipationsByAlias.get(p.volunteerAlias) || [];
        trackedParticipationsByAlias.set(p.volunteerAlias, [...existingParticipations, p]);
      });

    // Helper function to determine default shifts for new attendees
    const getDefaultShiftsForNewAttendee = (): Shift[] => {
      // Unlisted event mode: return placeholder shift using event title from wizard step 0
      if (isUnlistedEventMode) {
        const placeholderShift: Shift = {
          shiftId: 'unlisted-shift-placeholder',
          name: event?.title || '',
          eventId: '',
          startDateTime: event?.startTimestamp ?? new Date(),
          endDateTime: event?.endTimestamp ?? new Date(),
          shiftType: ShiftType.UNLISTED_EVENT_SHIFT,
          maxSignup: 0,
          signupsRemaining: 0,
          complete: false,
          source: 'VC',
        };
        return [placeholderShift];
      }
      if (shiftIdFromTrackAttendanceWizard) {
        const wizardShift = shifts.find((s) => s.shiftId === shiftIdFromTrackAttendanceWizard);
        return wizardShift ? [wizardShift] : [];
      }
      const dropInShift = shifts.find((s) => s.shiftType === ShiftType.DROP_IN);
      return dropInShift ? [dropInShift] : [];
    };

    // Step 1: Build rows from participations (existing signups)
    const rowsFromParticipations: ValidAttendee[] = mergedValidationData.participations
      .map((p) => {
        if (!p.volunteerAlias || !p.volunteerAlias.trim() || !p.shiftId) {
          return null;
        }

        const shift = shifts.find((s) => s.shiftId === p.shiftId);
        if (!shift) {
          return null;
        }

        // Only lock if attendance has been tracked (not just signed up)
        const hasTrackedAttendance = alreadyTrackedShiftsByAlias
          .get(p.volunteerAlias)
          ?.has(p.shiftId);

        return {
          name:
            `${p.volunteerLastName || ''}, ${p.volunteerFirstName || ''}`.trim() || 'Unknown name',
          alias: p.volunteerAlias,
          shift,
          isLocked: !!hasTrackedAttendance,
          alreadyTrackedShiftIds: Array.from(
            alreadyTrackedShiftsByAlias.get(p.volunteerAlias) || []
          ),
        };
      })
      .filter((row): row is ValidAttendee => row !== null);

    // Step 2: Build rows from people with ONLY already-tracked attendance
    const participationAliases = new Set(
      mergedValidationData.participations.map((p) => p.volunteerAlias)
    );

    const rowsFromTrackedParticipations: ValidAttendee[] = Array.from(
      trackedParticipationsByAlias.entries()
    )
      .filter(([alias]) => !participationAliases.has(alias))
      .flatMap(([alias, trackedParticipations]) => {
        const firstParticipation = trackedParticipations[0];
        const name =
          `${firstParticipation.volunteerLastName || ''}, ${firstParticipation.volunteerFirstName || ''}`.trim() ||
          'Unknown name';
        const alreadyTrackedShiftIds = trackedParticipations
          .map((p) => p.shiftId)
          .filter((shiftId): shiftId is string => shiftId !== undefined);

        return getDefaultShiftsForNewAttendee().map((shift) => ({
          name,
          alias,
          shift,
          isLocked: false,
          alreadyTrackedShiftIds,
        }));
      });

    // Step 3: Build rows from people without participation (auto-assigned - unlocked)
    const rowsFromPeopleWithoutParticipation: ValidAttendee[] =
      mergedValidationData.peopleWithoutParticipation
        .filter((person) => person.alias)
        .flatMap((person) =>
          getDefaultShiftsForNewAttendee().map((shift) => ({
            name: `${person.lastName || ''}, ${person.firstName || ''}`.trim() || 'Unknown name',
            alias: person.alias as string,
            shift,
            isLocked: false,
            alreadyTrackedShiftIds: [],
          }))
        );

    // Step 4: Merge all rows
    const allRows = [
      ...rowsFromParticipations,
      ...rowsFromTrackedParticipations,
      ...rowsFromPeopleWithoutParticipation,
    ];

    // Step 5: Filter out rows where all shifts are already tracked
    // In unlisted event mode, skip this filter since there are no real shifts to check against
    const filteredRows = isUnlistedEventMode
      ? allRows
      : allRows.filter((row) => {
          const alreadyTrackedShifts = new Set(row.alreadyTrackedShiftIds || []);
          const availableShifts = shifts.filter(
            (shift) =>
              shift.shiftType !== ShiftType.WAITLIST && !alreadyTrackedShifts.has(shift.shiftId)
          );
          return availableShifts.length > 0;
        });

    // Step 6: Sync with multi-shift tab selections and single-shift edits
    const rowsByAlias = new Map<string, ValidAttendee[]>();
    filteredRows.forEach((row) => {
      const existing = rowsByAlias.get(row.alias) || [];
      rowsByAlias.set(row.alias, [...existing, row]);
    });

    const finalRows: ValidAttendee[] = [];
    rowsByAlias.forEach((userRows, alias) => {
      const multiShiftUser = multiShiftAttendees.find((a) => a.alias === alias);
      const singleShiftEdit = singleShiftEdits.get(alias);

      if (multiShiftUser) {
        // User is in multi-shift tab - use their current selections
        multiShiftUser.signedUpShifts.forEach((shift) => {
          const existingRow = userRows.find((r) => r.shift.shiftId === shift.shiftId);
          finalRows.push({
            name: multiShiftUser.name,
            alias: multiShiftUser.alias,
            shift,
            isLocked: existingRow?.isLocked || false,
            alreadyTrackedShiftIds: existingRow?.alreadyTrackedShiftIds || [],
          });
        });
      } else if (singleShiftEdit) {
        // User has single-shift edit - use edited shift
        const originalRow = userRows[0];
        finalRows.push({
          name: originalRow.name,
          alias: originalRow.alias,
          shift: singleShiftEdit,
          isLocked: false,
          alreadyTrackedShiftIds: originalRow.alreadyTrackedShiftIds || [],
        });
      } else {
        // User not in multi-shift tab - use original rows
        finalRows.push(...userRows);
      }
    });

    return finalRows;
  }, [
    mergedValidationData.trackedParticipations,
    mergedValidationData.participations,
    mergedValidationData.peopleWithoutParticipation,
    shiftIdFromTrackAttendanceWizard,
    shifts,
    alreadyTrackedShiftsByAlias,
    multiShiftAttendees,
    singleShiftEdits,
    isUnlistedEventMode,
    event,
  ]);

  // Build skipped tracked attendees (read-only, one row per shift for now. TODO: wait for clarification in https://taskei.amazon.dev/tasks/cce-6071)
  const skippedTrackedRows = useMemo((): AlreadyTrackedAttendeeRow[] => {
    return mergedValidationData.trackedParticipations
      .filter((p) => p.volunteerAlias && p.volunteerAlias.trim() !== '' && p.shiftId)
      .map((p) => ({
        alias: p.volunteerAlias,
        name: `${p.volunteerLastName || ''}, ${p.volunteerFirstName || ''}`.trim(),
        shiftId: p.shiftId,
        shift: shifts.find((s) => s.shiftId === p.shiftId),
        attendanceStatus: p.attendanceStatus,
        attendanceMinutes: p.attendanceMinutes,
      }));
  }, [mergedValidationData.trackedParticipations, shifts]);

  // Dynamic counts
  const multiShiftUserCount = mergedValidationData.multiShiftParticipations?.length || 0;
  const validCount = validAttendeeRows.length;
  const skippedCount = skippedTrackedRows.length;
  const invalidCount = failedIdentifiers.filter((item) => !item.isResolved).length;

  useEffect(() => {
    if (validCount === 0 && invalidCount === 0 && multiShiftUserCount === 0 && skippedCount > 0) {
      setActiveTabId(ModalTab.SKIPPED);
    }
  }, [validCount, invalidCount, multiShiftUserCount, skippedCount]);

  /**
   * Slices an array to return only the items for the current page.
   * Used for client-side pagination across all validation tabs.
   * Memoized to prevent unnecessary re-creation on every render.
   */
  const paginateItems = useCallback(
    <T,>(items: T[]): T[] => {
      const startIndex = (currentPageIndex - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      return items.slice(startIndex, endIndex);
    },
    [currentPageIndex, pageSize]
  );

  // Calculate total pages for current tab
  const getTotalPagesForCurrentTab = useCallback((): number => {
    let totalItems = 0;
    switch (activeTabId) {
      case ModalTab.INVALID:
        totalItems = failedIdentifiers.length;
        break;
      case ModalTab.MULTI_SHIFT:
        totalItems = multiShiftAttendees.length;
        break;
      case ModalTab.VALID:
        totalItems = validAttendeeRows.length;
        break;
      case ModalTab.SKIPPED:
        totalItems = skippedTrackedRows.length;
        break;
    }
    return Math.ceil(totalItems / pageSize);
  }, [
    activeTabId,
    failedIdentifiers.length,
    multiShiftAttendees.length,
    validAttendeeRows.length,
    skippedTrackedRows.length,
    pageSize,
  ]);

  // Paginated data for each tab
  // useMemo prevents unnecessary re-slicing when unrelated state changes (e.g., validation errors in invalid tab)
  const paginatedFailedIdentifiers = useMemo(
    () => paginateItems(failedIdentifiers),
    [failedIdentifiers, paginateItems]
  );

  const paginatedMultiShiftAttendees = useMemo(
    () => paginateItems(multiShiftAttendees),
    [multiShiftAttendees, paginateItems]
  );

  const paginatedValidAttendeeRows = useMemo(
    () => paginateItems(validAttendeeRows),
    [validAttendeeRows, paginateItems]
  );

  const paginatedSkippedTrackedRows = useMemo(
    () => paginateItems(skippedTrackedRows),
    [skippedTrackedRows, paginateItems]
  );

  const handleCorrectedValueChange = (id: number, newValue: string): void => {
    setFailedIdentifiers(
      failedIdentifiers.map((item) =>
        item.id === id ? { ...item, correctedValue: newValue } : item
      )
    );
  };

  const handleEditClick = (id: number): void => {
    setFailedIdentifiers(
      failedIdentifiers.map((item) =>
        item.id === id ? { ...item, isEditing: true, originalValue: item.correctedValue } : item
      )
    );
  };

  const handleCancelEdit = (id: number): void => {
    setFailedIdentifiers(
      failedIdentifiers.map((item) =>
        item.id === id ? { ...item, isEditing: false, correctedValue: item.originalValue } : item
      )
    );
  };

  const handleAcceptCorrection = async (id: number): Promise<void> => {
    // Capture the corrected value before async operations
    const item = failedIdentifiers.find((i) => i.id === id);
    if (!item) return;

    publishKatalMetric('attendance_bulkUploadRetryInvalidEntry', identifierType);

    const correctedValue = item.correctedValue;

    setFailedIdentifiers((prev) =>
      prev.map((i) => (i.id === id ? { ...i, isValidating: true } : i))
    );

    try {
      const response = await callQueryClientSingletonRedVelvetQuery(
        queryClient,
        {
          redVelvetQueryKey: [
            'getParticipationsByEventAndAttendees',
            {
              eventId: event.eventId,
              shiftId: shiftIdFromTrackAttendanceWizard,
              attendees: correctedValue,
              attendeeIdentifierType: identifierType,
              shiftEndedBefore: new Date(),
            },
          ],
        },
        getEnabledFeatures(features, searchParams)
      );

      if (response.failedIdentifiers.length === 0) {
        setResolvedResponses((prev) => [...prev, response]);
        setValidationError(null);

        // Use functional state update to avoid race conditions
        setFailedIdentifiers((prev) =>
          prev.map((i) =>
            i.id === id
              ? {
                  ...i,
                  isEditing: false,
                  isResolved: true,
                  isValidating: false,
                  error: t('bulkUpload.validation.resolved'),
                }
              : i
          )
        );
      } else {
        setValidationError(correctedValue);
        setFailedIdentifiers((prev) =>
          prev.map((i) => (i.id === id ? { ...i, isValidating: false, isEditing: false } : i))
        );
      }
    } catch (error) {
      setValidationError('internal');
      setFailedIdentifiers((prev) =>
        prev.map((i) => (i.id === id ? { ...i, isValidating: false, isEditing: false } : i))
      );
    }
  };

  const handleSkipAll = (): void => {
    // Remove all items (both resolved and unresolved)
    // Resolved items are automatically tracked, so they don't get lost
    // Unresolved items are simply discarded
    publishKatalMetric(
      'attendance_bulkUploadSkipAllInvalidEntries',
      failedIdentifiers.length.toString()
    );
    setFailedIdentifiers([]);
    // Auto move to Valid tab as there's no more invalid entries
    setActiveTabId(ModalTab.VALID);
  };

  const handleShiftChange = (attendeeAlias: string, selectedOptions: MultiselectProps.Options) => {
    const selectedShiftIds = selectedOptions.map((option) => option.value);
    const selectedShifts = shifts.filter((shift) => selectedShiftIds.includes(shift.shiftId));

    setMultiShiftAttendees((prev) =>
      prev.map((attendee) =>
        attendee.alias === attendeeAlias
          ? { ...attendee, signedUpShifts: selectedShifts }
          : attendee
      )
    );
  };

  const handleValidTabShiftChange = (alias: string, shiftId: string) => {
    const selectedShift = shifts.find((s) => s.shiftId === shiftId);
    if (!selectedShift) return;

    setSingleShiftEdits((prev) => {
      const newMap = new Map(prev);
      newMap.set(alias, selectedShift);
      return newMap;
    });
  };

  const shiftOptions: MultiselectProps.Options = shifts
    .filter((shift) => shift.shiftType !== ShiftType.WAITLIST)
    .map((shift: Shift) => ({
      label: shift.name,
      value: shift.shiftId,
      description:
        shift.startDateTime && asDateTimeString(shift.startDateTime, timezone, i18n.language),
    }));

  // Create column definitions using factories
  const invalidColumns = useInvalidEntryColumns(
    handleCorrectedValueChange,
    handleAcceptCorrection,
    handleCancelEdit,
    handleEditClick
  );

  const multiShiftColumns = useMultiShiftAttendeeColumns(shiftOptions, shifts, handleShiftChange);
  const validColumns = useValidAttendeeColumns(
    shiftOptions,
    shifts,
    isUnlistedEventMode,
    openFromTrackAttendanceWizard,
    event?.title
  );
  const skippedColumns = useAlreadyTrackedShiftColumns();

  const paginationControls = useMemo(
    () => ({
      pagination: (
        <Pagination
          currentPageIndex={currentPageIndex}
          pagesCount={getTotalPagesForCurrentTab()}
          onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
        />
      ),
      preferences: (
        <CollectionPreferences
          title={t('bulkUpload.pagination.preferencesTitle')}
          confirmLabel={t('bulkUpload.pagination.confirmLabel')}
          cancelLabel={t('bulkUpload.pagination.cancelLabel')}
          preferences={{
            pageSize,
          }}
          pageSizePreference={{
            title: t('bulkUpload.pagination.pageSizeTitle'),
            options: [
              { value: 5, label: t('bulkUpload.pagination.pageSize', { count: 5 }) },
              { value: 10, label: t('bulkUpload.pagination.pageSize', { count: 10 }) },
              { value: 25, label: t('bulkUpload.pagination.pageSize', { count: 25 }) },
              { value: 50, label: t('bulkUpload.pagination.pageSize', { count: 50 }) },
              { value: 100, label: t('bulkUpload.pagination.pageSize', { count: 100 }) },
            ],
          }}
          onConfirm={({ detail }) => {
            setPageSize(detail.pageSize || DEFAULT_PAGE_SIZE);
            setCurrentPageIndex(1);
          }}
        />
      ),
    }),
    [currentPageIndex, pageSize, t, getTotalPagesForCurrentTab]
  );

  // Compute visible tabs based on mode and data
  const visibleTabs = useMemo(() => {
    const allTabs = [
      {
        id: ModalTab.INVALID,
        label: (
          <StatusIndicator type="error">
            {t('bulkUpload.validation.invalidEntries', { count: invalidCount })}
          </StatusIndicator>
        ),
      },
      {
        id: ModalTab.MULTI_SHIFT,
        label: (
          <StatusIndicator type="warning">
            {t('bulkUpload.validation.multiShiftEntries', { count: multiShiftUserCount })}
          </StatusIndicator>
        ),
      },
      {
        id: ModalTab.VALID,
        label: (
          <StatusIndicator type="success">
            {t('bulkUpload.validation.validEntries', { count: validCount })}
          </StatusIndicator>
        ),
      },
      {
        id: ModalTab.SKIPPED,
        label: (
          <StatusIndicator type="stopped">
            {t('bulkUpload.validation.skippedTracked', { count: skippedCount })}
          </StatusIndicator>
        ),
      },
    ];

    // Determine which tabs to show based on mode
    const isTabVisible = (tabId: ModalTab): boolean => {
      // Skipped tab only visible when there are skipped entries
      if (tabId === ModalTab.SKIPPED) {
        return skippedCount > 0;
      }
      // Multi-shift tab hidden in unlisted event mode and single-shift mode
      if (tabId === ModalTab.MULTI_SHIFT) {
        return !isUnlistedEventMode && !openFromTrackAttendanceWizard;
      }
      // Invalid and Valid tabs always visible
      return true;
    };

    return allTabs.filter((tab) => isTabVisible(tab.id as ModalTab));
  }, [
    t,
    invalidCount,
    multiShiftUserCount,
    validCount,
    skippedCount,
    isUnlistedEventMode,
    openFromTrackAttendanceWizard,
  ]);

  const renderTableContent = () => {
    switch (activeTabId) {
      case ModalTab.INVALID:
        return (
          <SpaceBetween size="l">
            <Header
              variant="h3"
              actions={
                <Button variant="link" onClick={handleSkipAll}>
                  {t('bulkUpload.validation.skipAllButton')}
                </Button>
              }
            >
              {t('bulkUpload.validation.issuesHeader', {
                count: invalidCount,
              })}
            </Header>
            {validationError && (
              <Alert type="error" dismissible onDismiss={() => setValidationError(null)}>
                {validationError === 'internal'
                  ? t('bulkUpload.validation.errors.internal')
                  : t('bulkUpload.validation.errors.correctedValueInvalid', {
                      correctedValue: validationError,
                    })}
              </Alert>
            )}
            <Table
              columnDefinitions={invalidColumns}
              items={paginatedFailedIdentifiers}
              variant="borderless"
              trackBy="id"
              stickyHeader
              pagination={paginationControls.pagination}
              preferences={paginationControls.preferences}
            />
          </SpaceBetween>
        );
      case ModalTab.MULTI_SHIFT:
        return (
          <Table
            columnDefinitions={multiShiftColumns}
            header={
              <Header counter={`(${multiShiftUserCount})`} variant="h3">
                {t('bulkUpload.validation.tableHeaders.attendeeTitle')}
              </Header>
            }
            items={paginatedMultiShiftAttendees}
            loadingText={t('bulkUpload.loadingAttendees')}
            trackBy="alias"
            variant="borderless"
            wrapLines
            stickyHeader
            pagination={paginationControls.pagination}
            preferences={paginationControls.preferences}
          />
        );
      case ModalTab.VALID:
        return (
          <Table
            columnDefinitions={validColumns}
            header={
              <Header counter={`(${validCount})`} variant="h3">
                {t('bulkUpload.validation.tableHeaders.attendeeTitle')}
              </Header>
            }
            items={paginatedValidAttendeeRows}
            loadingText={t('bulkUpload.loadingAttendees')}
            trackBy={(item) => `${item.alias}-${item.shift.shiftId}`}
            variant="borderless"
            wrapLines
            stickyHeader
            pagination={paginationControls.pagination}
            preferences={paginationControls.preferences}
            submitEdit={async (item, column, newValue) => {
              if (column.id === 'shift' && newValue) {
                handleValidTabShiftChange(item.alias, (newValue as Shift).shiftId);
              }
            }}
          />
        );
      case ModalTab.SKIPPED:
        return (
          <Table
            columnDefinitions={skippedColumns}
            header={
              <Header counter={`(${skippedCount})`} variant="h3">
                {t('bulkUpload.validation.tableHeaders.attendeeTitle')}
              </Header>
            }
            items={paginatedSkippedTrackedRows}
            loadingText={t('bulkUpload.loadingAttendees')}
            trackBy={(item) => `${item.alias}-${item.shiftId}`}
            variant="borderless"
            wrapLines
            stickyHeader
            pagination={paginationControls.pagination}
            preferences={paginationControls.preferences}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="bulk-upload-modal-content">
      <div className="bulk-upload-modal-scrollable">
        <Container
          header={<Header variant="h3">{t('bulkUpload.validation.summaryHeader')}</Header>}
        >
          <Tabs activeTabId={activeTabId} onChange={handleTabChange} tabs={visibleTabs} />

          {renderTableContent()}
        </Container>
      </div>

      {/* Fixed: Footer buttons */}
      <Box padding={{ top: 'l' }}>
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onCancel}>
              {t('bulkUpload.buttons.cancel')}
            </Button>
            <Button variant="primary" onClick={onPrevious}>
              {t('bulkUpload.buttons.previous')}
            </Button>
            <Button
              disabled={invalidCount > 0 || validCount === 0}
              variant="primary"
              onClick={() => {
                // Store bulk participants from validAttendeeRows
                const isEventLevelTracking = !openFromTrackAttendanceWizard && !isUnlistedEventMode;

                const participationMap = new Map(
                  mergedValidationData.participations.map((p) => [p.volunteerAlias, p])
                );
                const peopleMap = new Map(
                  mergedValidationData.peopleWithoutParticipation.map((p) => [p.alias, p])
                );

                const bulkParticipantData: Record<string, Amazonian> = {};

                validAttendeeRows.forEach((row) => {
                  // Event-level: use composite key 'alias-shiftId' to track each person per shift
                  // Single-shift: use 'alias' since there's only one shift
                  const key = isEventLevelTracking
                    ? `${row.alias}-${row.shift.shiftId}`
                    : row.alias;
                  const participation = participationMap.get(row.alias);
                  const person = peopleMap.get(row.alias);

                  bulkParticipantData[key] = {
                    alias: row.alias,
                    name: row.name,
                    building: participation?.volunteerBuildingName || person?.building,
                    managerLogin: participation?.volunteerManagerAlias || person?.managerLogin,
                    ...(isEventLevelTracking && {
                      shiftId: row.shift.shiftId,
                      originalShiftId: row.shift.shiftId,
                      shiftName: row.shift.name,
                      shiftDate: formatShiftDate(row.shift.startDateTime),
                      shiftTime: formatShiftTime(row.shift.startDateTime),
                    }),
                    signedUp: row.isLocked,
                    attendance: 'ATTENDED',
                    trackedTime: 0,
                    trackedBy: user.userId,
                  };
                });

                updateBulkParticipants(bulkParticipantData);

                // Reasoning:
                // - participations includes single/multi signups attendees
                // - participations and tracked participation and peopleWithoutParticipation are mutually exclusive
                // - we can deduce duplicate attendees = user-added entries - (participations + trackedParticipations + peopleWithoutParticipation)
                publishKatalMetric(
                  `attendance_bulkUploadBulkAddValidationSuccessCount`,
                  (
                    (validationData.participations?.length ?? 0) +
                    (validationData.trackedParticipations?.length ?? 0) +
                    (validationData.peopleWithoutParticipation?.length ?? 0)
                  ).toString()
                );
                publishKatalMetric(
                  'attendance_bulkUploadAddAttendeesFromBulkAdd',
                  validCount.toString()
                );

                onComplete?.();
              }}
            >
              {t('bulkUpload.buttons.bulkAddAttendees', {
                count: validCount,
              })}
            </Button>
          </SpaceBetween>
        </Box>
      </Box>
    </div>
  );
}

/**
 * Builds attendees from backend multiShiftParticipations.
 * Groups participations by alias and prepopulates their shifts.
 * Filters out entries without valid names.
 */
function buildAttendeesFromMultiShift(
  multiShiftParticipations: MultiShiftParticipation[],
  shifts: Shift[],
  alreadyTrackedShiftsByAlias: Map<string, Set<string>>
): MultiShiftAttendee[] {
  return multiShiftParticipations
    .filter((ms) => ms.alias && ms.alias.trim() !== '')
    .map((ms) => {
      const firstParticipation = ms.participations[0];
      const firstName = firstParticipation?.volunteerFirstName || '';
      const lastName = firstParticipation?.volunteerLastName || '';
      const name = `${lastName}, ${firstName}`.trim();

      // Skip if no valid name (empty or just comma)
      if (!name || name === ',') {
        return null;
      }

      // Prepopulate shifts from all participations
      const signedUpShifts = ms.participations
        .map((p) => shifts.find((s) => s.shiftId === p.shiftId))
        .filter((shift): shift is Shift => shift !== undefined);

      // Get already-tracked shifts for this user
      const alreadyTrackedShifts = alreadyTrackedShiftsByAlias.get(ms.alias) || new Set<string>();

      return {
        name,
        alias: ms.alias,
        signedUpShifts,
        alreadyTrackedShiftIds: Array.from(alreadyTrackedShifts),
      };
    })
    .filter((attendee): attendee is MultiShiftAttendee => attendee !== null);
}

/**
 * Merges backend attendees with existing attendees, preserving manual shift selections.
 * For existing attendees (by alias), keeps their current signedUpShifts.
 * For new attendees, uses backend data.
 */
function mergeAttendeesPreservingManualChanges(
  backendAttendees: MultiShiftAttendee[],
  existingAttendees: MultiShiftAttendee[]
): MultiShiftAttendee[] {
  return backendAttendees.map((backendAttendee) => {
    const existing = existingAttendees.find((a) => a.alias === backendAttendee.alias);
    if (existing) {
      // Preserve manual selections
      return existing;
    }
    // New attendee from correction, use backend data
    return backendAttendee;
  });
}
